'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';

import { Icon } from '@/lib/ui-kit/icons';
import { ColorIcon, type ColorIconName } from '@/lib/ui-kit/icons/color';
import { cn } from '@/lib/ui-kit/utils';

interface LanguageOption {
  code: string;
  display: string;
  label: string;
  flag: ColorIconName;
}

/** M — the desktop menus' row; L — the burger drawer's (Figma 15071:148531). */
type LanguageRowSize = 'M' | 'L';

interface LanguageRowProps {
  options: LanguageOption[];
  selectedCode: string;
  onSelect: (code: string) => void;
  /** The row's title, translated by the caller. */
  label: string;
  size?: LanguageRowSize;
  disabled?: boolean;
}

interface LanguageItemProps {
  option: LanguageOption;
  index: number;
  isActive: boolean;
  size: LanguageRowSize;
  disabled?: boolean;
  onPick: (code: string) => void;
}

const SIZE_CLASSES: Record<
  LanguageRowSize,
  {
    trigger: string;
    label: string;
    value: string;
    currentFlag: string;
    list: string;
    item: string;
    itemText: string;
  }
> = {
  M: {
    trigger: 'py-1',
    label: 'text-[13px] font-normal leading-4 tracking-[-0.13px]',
    value: 'text-[13px] font-normal leading-4 tracking-[-0.13px]',
    currentFlag: 'h-3 w-[18px] rounded-[2px]',
    list: 'gap-1 pt-1',
    item: 'px-1 py-1',
    itemText: 'text-[13px] leading-4 tracking-[-0.13px]',
  },
  L: {
    trigger: 'h-12',
    label: 'text-[15px] font-medium leading-5',
    value: 'text-[15px] font-medium leading-5',
    currentFlag: 'h-4 w-6 rounded-[3px]',
    list: '',
    item: 'h-11 pl-3',
    itemText: 'text-[14px] leading-[19px]',
  },
};

const LIST_TRANSITION = { duration: 0.2, ease: 'easeOut' } as const;
const FLAG_SHADOW = 'shadow-[0_1px_2px_rgba(0,0,0,0.15)] dark:shadow-none';

function LanguageItem({ option, index, isActive, size, disabled, onPick }: LanguageItemProps) {
  const classes = SIZE_CLASSES[size];
  const handleClick = () => onPick(option.code);

  return (
    <motion.li
      initial={{ opacity: 0, x: 8 }}
      animate={{
        opacity: 1,
        x: 0,
        transition: { delay: 0.04 + index * 0.03, duration: 0.18, ease: [0.23, 1, 0.32, 1] },
      }}
      // No staggered exit: the list collapses as one, without waiting on its rows.
      exit={{ opacity: 0, transition: { duration: 0.1 } }}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={handleClick}
        className={cn(
          'group flex w-full cursor-pointer items-center gap-2 rounded-md text-left outline-none',
          'disabled:cursor-not-allowed disabled:opacity-50',
          classes.item,
        )}
      >
        <span
          className={cn('relative h-4 w-6 shrink-0 overflow-hidden rounded-[3px]', FLAG_SHADOW)}
        >
          <ColorIcon name={option.flag} size="100%" className="absolute inset-0" aria-hidden />
        </span>
        <span
          className={cn(
            'flex-1 font-normal transition-colors group-hover:text-v2-bg-blue',
            classes.itemText,
            isActive ? 'text-v2-bg-blue' : 'text-v2-text-primary',
          )}
        >
          {option.label}
        </span>
        <span className="shrink-0 text-[11px] font-normal leading-4 text-v2-text-secondary tabular-nums transition-colors group-hover:text-v2-bg-blue">
          {option.display}
        </span>
      </button>
    </motion.li>
  );
}

function LanguageRow({
  options,
  selectedCode,
  onSelect,
  label,
  size = 'M',
  disabled,
}: LanguageRowProps) {
  const [open, setOpen] = useState(false);
  const classes = SIZE_CLASSES[size];
  const current = options.find((o) => o.code === selectedCode) ?? options[0];

  const handleToggle = () => setOpen((prev) => !prev);
  const handlePick = (code: string) => {
    onSelect(code);
    setOpen(false);
  };

  return (
    <div className="flex w-full flex-col">
      <button
        type="button"
        onClick={handleToggle}
        className={cn(
          'group flex w-full cursor-pointer items-center gap-2 outline-none',
          classes.trigger,
        )}
        aria-expanded={open}
      >
        <span
          className={cn(
            'flex-1 text-left text-v2-text-primary group-hover:text-v2-bg-blue',
            classes.label,
          )}
        >
          {label}
        </span>
        <span className="flex shrink-0 items-center gap-1">
          {current && (
            <span
              className={cn('relative shrink-0 overflow-hidden', classes.currentFlag, FLAG_SHADOW)}
            >
              <ColorIcon name={current.flag} size="100%" className="absolute inset-0" aria-hidden />
            </span>
          )}
          <span className={cn('flex items-center gap-0.5 text-v2-text-primary', classes.value)}>
            {current?.display ?? selectedCode.toUpperCase()}
            {size === 'L' ? (
              <Icon
                name="ArrowSmallDown2"
                size={20}
                className={cn(
                  'text-v2-text-secondary transition-transform duration-200',
                  open && 'rotate-180',
                )}
              />
            ) : (
              <ChevronDown
                className={cn(
                  'size-4 text-v2-icon-primary transition-transform',
                  open && 'rotate-180',
                )}
                strokeWidth={2}
              />
            )}
          </span>
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          // The gap above the list is padding inside the animated box: a margin
          // outside it stayed 4px tall after the collapse and then snapped away.
          <motion.div
            key="lang-list"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={LIST_TRANSITION}
            className="overflow-hidden"
          >
            <ul className={cn('flex flex-col', classes.list)}>
              {options.map((option, index) => (
                <LanguageItem
                  key={option.code}
                  option={option}
                  index={index}
                  isActive={option.code === selectedCode}
                  size={size}
                  disabled={disabled}
                  onPick={handlePick}
                />
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export type { LanguageOption, LanguageRowProps };
export { LanguageRow };
